import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, deleteField, writeBatch, Timestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useApp } from '../App';
import {
  Positions, DetailedPositions, basePositionOf, positionLabel, DefaultProfileVisibility,
} from '../utils/constants';

export default function ProfilePanel() {
  const { user, loadUserData } = useApp();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 민감 칸은 users/{uid}/private/profile 에 있다(W1-08). 둘을 읽어 private 값으로 덮는다.
  const loadProfile = async () => {
    try {
      const [pub, priv] = await Promise.all([
        getDoc(doc(db, 'users', user.uid)),
        getDoc(doc(db, 'users', user.uid, 'private', 'profile')).catch(() => null),
      ]);
      const d = {
        id: user.uid,
        ...(pub.exists() ? pub.data() : {}),
        ...(priv && priv.exists() ? priv.data() : {}),
        email: auth.currentUser?.email || null,   // 공개 문서에 email 을 두지 않는다
      };
      setProfile(d); setForm(d);
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const detailed = (form.positions || []).filter(x => DetailedPositions[x]);
      // position 은 F/D/G 만. 비어 있으면 세부 포지션에서 끌어온다(W1-09).
      const base = Positions[form.position] ? form.position : basePositionOf(detailed[0]);

      const batch = writeBatch(db);

      // ① 민감 칸 — 본인만 읽는 private 문서
      batch.set(doc(db, 'users', user.uid, 'private', 'profile'), {
        height: form.height ? parseInt(form.height) : null,
        weight: form.weight ? parseInt(form.weight) : null,
        phoneNumber: form.phoneNumber || null,
        visibility: form.visibility || DefaultProfileVisibility,
        updatedAt: Timestamp.now(),
      }, { merge: true });

      // ② 공개 문서 — 민감 칸은 지운다
      batch.set(doc(db, 'users', user.uid), {
        name: form.name || '',
        jerseyNumber: form.jerseyNumber ? parseInt(form.jerseyNumber) : null,
        position: base || null,
        positions: detailed,
        dominantHand: form.dominantHand || null,
        hockeyStartYear: form.hockeyStartYear ? parseInt(form.hockeyStartYear) : null,
        updatedAt: Timestamp.now(),
        height: deleteField(),
        weight: deleteField(),
        phoneNumber: deleteField(),
        email: deleteField(),
        birthDate: deleteField(),
      }, { merge: true });

      // ③ users/{uid}/shared/friends (친구 공개 사본)는 보류 — 회신 문서 참조.
      //    iOS ProfilePrivacy.swift:146-153 의 칸 구성을 확인하지 못했고,
      //    이 문서는 통째로 교체되므로 형식을 잘못 쓰면 iOS 가 쓴 값을 망친다.

      await batch.commit();
      setProfile({ ...profile, ...form, position: base || null, positions: detailed });
      setEditing(false);
      if (loadUserData) await loadUserData(user.uid);
      await loadProfile();
    } catch (err) { alert('저장 실패: ' + err.message); }
    setSaving(false);
  };

  const currentYear = new Date().getFullYear();
  const expYears = profile?.hockeyStartYear ? currentYear - profile.hockeyStartYear : null;

  return (
    <div style={{ maxWidth: 700 }}>
      <div style={s.panel}>
        <div style={{ ...s.ph, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={s.pt}>👤 내 프로필</h2>
          {!editing ? <button onClick={() => setEditing(true)} style={s.editBtn}>수정</button>
            : <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setEditing(false); setForm(profile); }} style={s.cancelBtn}>취소</button>
                <button onClick={handleSave} disabled={saving} style={s.saveBtn}>{saving ? '저장 중...' : '저장'}</button>
              </div>}
        </div>
        <div style={{ padding: 24 }}>
          {profile ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 28 }}>
                <div style={s.avatarXl}>{(profile.name || '?')[0].toUpperCase()}</div>
                <div>
                  {editing ? <input style={{ ...s.input, fontSize: 18, fontWeight: 600 }} value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="이름" />
                    : <h3 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{profile.jerseyNumber ? '#' + profile.jerseyNumber + ' ' : ''}{profile.name}</h3>}
                  <div style={{ fontSize: 13, color: '#778899', marginTop: 4 }}>{profile.email}</div>
                </div>
              </div>

              <Sec title="🏒 하키 정보">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  {editing ? <>
                    <FF label="등번호"><input type="number" style={s.input} value={form.jerseyNumber || ''} onChange={e => setForm(p => ({ ...p, jerseyNumber: e.target.value }))} /></FF>
                    <FF label="포지션"><select style={s.input} value={form.position || ''} onChange={e => setForm(p => ({ ...p, position: e.target.value }))}><option value="">선택</option>{Object.entries(Positions).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></FF>
                    <FF label="주로 쓰는 손"><select style={s.input} value={form.dominantHand || ''} onChange={e => setForm(p => ({ ...p, dominantHand: e.target.value }))}><option value="">선택</option><option value="L">왼손</option><option value="R">오른손</option></select></FF>
                  </> : <>
                    <IB label="등번호" value={profile.jerseyNumber ? '#' + profile.jerseyNumber : '-'} />
                    <IB label="포지션" value={positionLabel(profile.position, profile.positions) || '-'} />
                    <IB label="주로 쓰는 손" value={profile.dominantHand === 'L' ? '왼손' : profile.dominantHand === 'R' ? '오른손' : '-'} />
                  </>}
                </div>

                {/* 세부 포지션 — iOS users/{uid}.positions 배열 (W1-09) */}
                {editing && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, color: '#778899', marginBottom: 8, fontWeight: 600 }}>
                      세부 포지션 <span style={{ fontWeight: 400 }}>(여러 개 고를 수 있습니다)</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {Object.entries(DetailedPositions).map(([k, v]) => {
                        const on = (form.positions || []).includes(k);
                        return (
                          <button key={k} type="button"
                            onClick={() => setForm(prev => {
                              const cur = prev.positions || [];
                              const next = on ? cur.filter(x => x !== k) : [...cur, k];
                              return {
                                ...prev,
                                positions: next,
                                position: Positions[prev.position] ? prev.position : basePositionOf(next[0]),
                              };
                            })}
                            style={{
                              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              border: `1px solid ${on ? '#5BC4D8' : 'rgba(255,255,255,0.1)'}`,
                              backgroundColor: on ? 'rgba(91,196,216,0.12)' : 'transparent',
                              color: on ? '#5BC4D8' : '#8899AA',
                            }}>{v.label}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Sec>

              <Sec title="📏 신체 정보">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  {editing ? <>
                    <FF label="키 (cm)"><input type="number" style={s.input} value={form.height || ''} onChange={e => setForm(p => ({ ...p, height: e.target.value }))} /></FF>
                    <FF label="몸무게 (kg)"><input type="number" style={s.input} value={form.weight || ''} onChange={e => setForm(p => ({ ...p, weight: e.target.value }))} /></FF>
                    <FF label="시작년도"><input type="number" style={s.input} value={form.hockeyStartYear || ''} onChange={e => setForm(p => ({ ...p, hockeyStartYear: e.target.value }))} placeholder="예: 2018" /></FF>
                  </> : <>
                    <IB label="키" value={profile.height ? profile.height + 'cm' : '-'} />
                    <IB label="몸무게" value={profile.weight ? profile.weight + 'kg' : '-'} />
                    <IB label="하키 경력" value={expYears != null ? expYears + '년' : '-'} />
                  </>}
                </div>
              </Sec>

              <Sec title="📞 연락처">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {editing ? <>
                    <FF label="이메일"><div style={{ fontSize: 14, color: '#778899', padding: '10px 0' }}>{profile.email}</div></FF>
                    <FF label="전화번호"><input style={s.input} value={form.phoneNumber || ''} onChange={e => setForm(p => ({ ...p, phoneNumber: e.target.value }))} placeholder="010-0000-0000" /></FF>
                  </> : <>
                    <IB label="이메일" value={profile.email || '-'} />
                    <IB label="전화번호" value={profile.phoneNumber || '-'} />
                  </>}
                </div>
              </Sec>

              {profile.equipment && (
                <Sec title="⛸️ 장비 정보">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {Object.entries(profile.equipment).filter(([_, v]) => v).map(([k, v]) => {
                      const labels = { skates: '스케이트', stick: '스틱', helmet: '헬멧', gloves: '글러브', pants: '바지', shoulderPads: '숄더패드', elbowPads: '엘보패드', shinGuards: '신가드' };
                      return <IB key={k} label={labels[k] || k} value={v} />;
                    })}
                  </div>
                </Sec>
              )}

              {profile.watchType && (
                <Sec title="⌚ 스마트워치">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <IB label="워치 종류" value={profile.watchType === 'garmin' ? 'Garmin' : profile.watchType} />
                    <IB label="연결 상태" value={profile.isWatchConnected ? '연결됨 ✅' : '연결 안됨'} />
                  </div>
                </Sec>
              )}
            </>
          ) : <div style={s.empty}>프로필을 불러오는 중...</div>}
        </div>
      </div>
    </div>
  );
}

function Sec({ title, children }) { return <div style={{ marginBottom: 24 }}><div style={{ fontSize: 14, fontWeight: 600, color: '#5BC4D8', marginBottom: 12 }}>{title}</div>{children}</div>; }
function IB({ label, value }) { return <div style={{ padding: 12, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.02)' }}><div style={{ fontSize: 11, color: '#667788', fontWeight: 600, marginBottom: 4 }}>{label}</div><div style={{ fontSize: 14, fontWeight: 500 }}>{value}</div></div>; }
function FF({ label, children }) { return <div><div style={{ fontSize: 11, color: '#667788', fontWeight: 600, marginBottom: 4 }}>{label}</div>{children}</div>; }

const s = {
  panel: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(91,196,216,0.06)', borderRadius: 12, overflow: 'hidden' },
  ph: { padding: '14px 20px', borderBottom: '1px solid rgba(91,196,216,0.06)' }, pt: { margin: 0, fontSize: 15, fontWeight: 600 },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#556677', fontSize: 14 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(91,196,216,0.1)', fontSize: 14, backgroundColor: 'rgba(10,18,32,0.8)', color: '#E8ECF1', boxSizing: 'border-box' },
  editBtn: { padding: '6px 16px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#5BC4D8', fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  saveBtn: { padding: '6px 16px', borderRadius: 6, border: 'none', backgroundColor: '#5BC4D8', color: '#0A1220', fontSize: 12, cursor: 'pointer', fontWeight: 700 },
  cancelBtn: { padding: '6px 16px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#8899AA', fontSize: 12, cursor: 'pointer' },
  avatarXl: { width: 64, height: 64, borderRadius: '50%', backgroundColor: 'rgba(91,196,216,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, color: '#5BC4D8', border: '3px solid #5BC4D8', flexShrink: 0 },
};
